import type { PropsWithChildren } from "react";
import { useEffect, useRef, useState } from "react";

export type RevealDirection = "up" | "left" | "right";

const animateByDirection: Record<RevealDirection, string> = {
	up: "animate-marketing-reveal-up",
	left: "animate-marketing-reveal-left",
	right: "animate-marketing-reveal-right",
};

export type RevealOnViewProps = PropsWithChildren<{
	className?: string;
	delayMs?: number;
	from?: RevealDirection;
}>;

export function RevealOnView({ children, className = "", delayMs = 0, from = "up" }: RevealOnViewProps) {
	const ref = useRef<HTMLDivElement>(null);
	const [shown, setShown] = useState(false);

	useEffect(() => {
		const node = ref.current;
		if (!node) {
			return;
		}

		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
			setShown(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				const first = entries[0];
				if (first?.isIntersecting) {
					setShown(true);
					observer.disconnect();
				}
			},
			{ root: null, rootMargin: "0px 0px -7% 0px", threshold: [0, 0.06, 0.12] },
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, []);

	const hidden = !shown;
	const style = shown && delayMs > 0 ? { animationDelay: `${delayMs}ms` } : undefined;

	return (
		<div ref={ref} className={`${hidden ? "opacity-0" : animateByDirection[from]} ${className}`.trim()} style={style}>
			{children}
		</div>
	);
}
